import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listContactEnquiries,
  type ContactEnquiryRow,
} from "@/lib/admin/enquiries.functions";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminDataToolbar } from "@/components/admin/import/AdminDataToolbar";
import { Pager } from "@/components/admin/Pager";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute("/_admin/admin/enquiries")({
  component: EnquiriesPage,
});

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function EnquiriesPage() {
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();
  const list = useServerFn(listContactEnquiries);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin", "contact-enquiries", page],
    queryFn: () => list({ data: { page } }),
  });

  return (
    <div>
      <AdminPageHeader
        title="Contact enquiries"
        description="Newest first."
        actions={
          <AdminDataToolbar
            exportName="contact-enquiries"
            moduleId="enquiries"
            rows={() => (data?.rows ?? []).map((r) => ({ received: r.created_at, name: r.name, email: r.email, phone: r.phone ?? "", message: r.message }))}
            onImported={() =>
              queryClient.invalidateQueries({ queryKey: ["admin", "contact-enquiries"] })
            }
          />
        }
      />
      {isError ? (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            Couldn't load enquiries. Please refresh and try again.
          </AlertDescription>
        </Alert>
      ) : null}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : data && data.rows.length > 0 ? (
        <>
          <div className="hidden overflow-hidden rounded-md border md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Received</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Message</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r: ContactEnquiryRow) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {formatDate(r.created_at)}
                    </TableCell>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="text-sm">{r.email}</TableCell>
                    <TableCell className="text-sm">{r.phone || "—"}</TableCell>
                    <TableCell className="max-w-md whitespace-pre-wrap text-sm text-muted-foreground">
                      {r.message}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <ul className="space-y-3 md:hidden">
            {data.rows.map((r) => (
              <li key={r.id} className="rounded-md border p-3">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{formatDate(r.created_at)}</span>
                </div>
                <div className="mt-1 font-medium text-foreground">{r.name}</div>
                <div className="text-sm text-muted-foreground">{r.email}</div>
                {r.phone ? (
                  <div className="text-sm text-muted-foreground">{r.phone}</div>
                ) : null}
                <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">
                  {r.message}
                </p>
              </li>
            ))}
          </ul>
          <Pager
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPageChange={setPage}
          />
        </>
      ) : (
        <p className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          No enquiries yet.
        </p>
      )}
    </div>
  );
}